import React from 'react';
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  BarChart as ReBarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  TooltipProps
} from 'recharts';
import './Charts.css';

// ---------- Token-backed palette -----------------------------------------

const PALETTE = [
  '#4F7FFF', // accent
  '#34D399', // success
  '#FBBF24', // warning
  '#60A5FA', // info
  '#F87171', // danger
  '#A78BFA', // extra 1
  '#F59E0B', // extra 2
  '#14B8A6'  // extra 3
];

const AXIS_COLOR = '#7A8294';
const GRID_COLOR = '#262B35';
const TICK_FONT = { fill: AXIS_COLOR, fontSize: 11 };

// ---------- Shared Tooltip ----------------------------------------------

const ChartTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bso-chart__tooltip">
      {label != null && <div className="bso-chart__tooltip-label">{String(label)}</div>}
      {payload.map((entry, i) => (
        <div key={i} className="bso-chart__tooltip-row">
          <span className="bso-chart__tooltip-dot" style={{ background: (entry.color as string) || PALETTE[i % PALETTE.length] }} />
          <span>{entry.name ?? 'value'}:</span>
          <strong>{typeof entry.value === 'number' ? entry.value.toLocaleString() : String(entry.value)}</strong>
        </div>
      ))}
    </div>
  );
};

// ---------- LineChart ----------------------------------------------------

export interface LineChartSeries {
  key: string;
  label?: string;
  color?: string;
}

export interface LineChartProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  series: LineChartSeries[];
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  emptyState?: React.ReactNode;
  className?: string;
}

export const LineChart: React.FC<LineChartProps> = ({
  data, xKey, series, height = 240, showGrid = true, showLegend = false, emptyState, className
}) => {
  if (!data.length) return <div className={`bso-chart ${className ?? ''}`}><div className="bso-chart__empty">{emptyState ?? 'No data'}</div></div>;
  return (
    <div className={`bso-chart ${className ?? ''}`} style={{ height }}>
      <ResponsiveContainer>
        <ReLineChart data={data}>
          {showGrid && <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />}
          <XAxis dataKey={xKey} stroke={AXIS_COLOR} tick={TICK_FONT} axisLine={false} tickLine={false} />
          <YAxis stroke={AXIS_COLOR} tick={TICK_FONT} axisLine={false} tickLine={false} />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: AXIS_COLOR, strokeOpacity: 0.3 }} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 12, color: AXIS_COLOR }} />}
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label ?? s.key}
              stroke={s.color ?? PALETTE[i % PALETTE.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </ReLineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------- BarChart -----------------------------------------------------

export interface BarChartProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  series: LineChartSeries[];
  stacked?: boolean;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  emptyState?: React.ReactNode;
  className?: string;
}

export const BarChart: React.FC<BarChartProps> = ({
  data, xKey, series, stacked, height = 240, showGrid = true, showLegend = false, emptyState, className
}) => {
  if (!data.length) return <div className={`bso-chart ${className ?? ''}`}><div className="bso-chart__empty">{emptyState ?? 'No data'}</div></div>;
  return (
    <div className={`bso-chart ${className ?? ''}`} style={{ height }}>
      <ResponsiveContainer>
        <ReBarChart data={data}>
          {showGrid && <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />}
          <XAxis dataKey={xKey} stroke={AXIS_COLOR} tick={TICK_FONT} axisLine={false} tickLine={false} />
          <YAxis stroke={AXIS_COLOR} tick={TICK_FONT} axisLine={false} tickLine={false} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(79, 127, 255, 0.06)' }} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 12, color: AXIS_COLOR }} />}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label ?? s.key}
              fill={s.color ?? PALETTE[i % PALETTE.length]}
              radius={stacked ? 0 : [4, 4, 0, 0]}
              stackId={stacked ? 'a' : undefined}
            />
          ))}
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------- DonutChart ---------------------------------------------------

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color?: string;
}

export interface DonutChartProps {
  slices: DonutSlice[];
  height?: number;
  centerLabel?: React.ReactNode;
  centerValue?: React.ReactNode;
  showLegend?: boolean;
  emptyState?: React.ReactNode;
  className?: string;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  slices, height = 240, centerLabel, centerValue, showLegend = true, emptyState, className
}) => {
  if (!slices.length || slices.every((s) => !s.value)) {
    return <div className={`bso-chart ${className ?? ''}`}><div className="bso-chart__empty">{emptyState ?? 'No data'}</div></div>;
  }
  return (
    <div className={`bso-chart ${className ?? ''}`} style={{ height, position: 'relative' }}>
      <ResponsiveContainer>
        <RePieChart>
          <Tooltip content={<ChartTooltip />} />
          <Pie
            data={slices}
            dataKey="value"
            nameKey="label"
            innerRadius="60%"
            outerRadius="90%"
            paddingAngle={1}
            stroke="none"
          >
            {slices.map((s, i) => (
              <Cell key={s.key} fill={s.color ?? PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
          {showLegend && <Legend wrapperStyle={{ fontSize: 12, color: AXIS_COLOR }} />}
        </RePieChart>
      </ResponsiveContainer>
      {(centerLabel || centerValue) && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          {centerValue && <div style={{ fontSize: 'var(--bso-text-2xl)', fontWeight: 600 }}>{centerValue}</div>}
          {centerLabel && <div style={{ fontSize: 11, color: AXIS_COLOR, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{centerLabel}</div>}
        </div>
      )}
    </div>
  );
};

// ---------- SparkLine ----------------------------------------------------

export interface SparkLineProps {
  values: number[];
  color?: string;
  width?: number;
  height?: number;
  'aria-label'?: string;
}

export const SparkLine: React.FC<SparkLineProps> = ({
  values, color = PALETTE[0], width = 96, height = 28, ...rest
}) => {
  if (values.length < 2) {
    return (
      <svg className="bso-sparkline" width={width} height={height} aria-label={rest['aria-label']} role="img">
        <line x1={0} y1={height / 2} x2={width} y2={height / 2} stroke={GRID_COLOR} strokeWidth={1} />
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pad = 2;
  const step = (width - pad * 2) / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = pad + i * step;
      const y = pad + ((max - v) / range) * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className="bso-sparkline" width={width} height={height} aria-label={rest['aria-label']} role="img">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};
